import React, { useState, useEffect } from 'react';
import { ArrowRight, CircleQuestionMark } from 'lucide-react';
import { useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Modal from 'src/shared/components/modal/Modal';
import { CMSSpecies } from 'src/shared/types';
import Show from 'src/shared/components/show/Show';
import Loader from 'src/shared/components/loader/Loader';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState } from 'src/shared/components/data-table/dataTableTypes';
import StatusChip from 'src/shared/components/status-chip/StatusChip';
import { iucnStatusData, isRedListCategory } from 'src/shared/helpers/iucnStatusHelper';
import { Status } from 'src/shared/types/status';
import { Badge } from 'src/shared/components/ui/badge';
import { PrimaryButton } from 'src/shared/components/button/Button';
import useBoolean from 'src/shared/hooks/useBoolean';
import cmsService from 'src/services/cms-service';
import Tabs from 'src/shared/components/Tabs/Tabs';
import { modalTablesMergeComparator } from 'src/app/portfolio/helpers/portfolioHelpers';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
import BookCallModal from 'src/app/portfolio/components/book-call-modal/BookCallModal';
import DetectionBadge, { DetectionBadgeType } from 'src/app/portfolio/components/detection-badge/DetectionBadge';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { TableUpsellSection } from 'src/app/portfolio/components/table-upsell-section/TableUpsellSection';
import { getConfidenceDetail } from 'src/shared/helpers/speciesConfidence';
import { useSpeciesDetail } from 'src/app/portfolio/hooks/useSpeciesDetail';

type SpeciesInfoModalProps = {
    onClose?: () => void;
    commonName: string;
    scientificName: string;
    /**
     * The species' IUCN Red List category (e.g. 'NT'), always the real one —
     * IUCN category and invasive risk are independent facts, each with its own
     * header field, so no list may hide the category behind a placeholder.
     */
    iucnStatus?: string;
    /**
     * The list the row that opened this modal belongs to — the lens the site
     * table opens on. The table itself carries a tab per applicable lens
     * (all / Red List / invasive), so every lens is one click away. Absent
     * opens on all sites.
     */
    initialTab?: 'redList' | 'invasive';
    /**
     * How many sites the portfolio has — the denominator for the header's
     * coverage figure ("37% (3 sites)"), matching the list pages' column.
     * Absent falls back to plain occurrence.
     */
    totalSiteCount?: number;
};

/** One way of reading the same site set — never different data (DD-5796). */
type SiteLens = 'all' | 'redList' | 'invasive';

const confidenceOrder: { [key: string]: number } = {
    high: 0,
    medium: 1,
    low: 2,
};

const tableColumns: DataTableColumns = [
    {
        columnId: 'siteName',
        title: 'Site name',
        width: '30%',
    },
    {
        columnId: 'confidence',
        title: 'Prediction Confidence',
        width: '20%',
        titleSlug: 'species-risk-site-confidence',
    },
    {
        columnId: 'nmSamples',
        title: 'NatureMetrics Samples',
        width: '25%',
        titleSlug: 'species-risk-site-nm-samples',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.BookCall ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.BookCall),
            content: <TableUpsellSection type={DetectionBadgeType.BookCall} />,
        },
    },
    {
        columnId: 'uploadedData',
        title: 'Uploaded Sample Data',
        width: '25%',
        titleSlug: 'species-risk-site-uploaded-data',
        renderer: ({ cellData }) => {
            if (typeof cellData !== 'string') {
                return null;
            }
            return (
                <DetectionBadge
                    type={cellData as DetectionBadgeType}
                    iconPosition={cellData === DetectionBadgeType.UploadData ? 'end' : 'start'}
                />
            );
        },
        columnBanner: {
            shouldShow: ({ rows, columnIndex }) => modalTablesMergeComparator(rows, columnIndex, DetectionBadgeType.UploadData),
            content: <TableUpsellSection type={DetectionBadgeType.UploadData} />,
        },
    },
];

const SpeciesInfoModal = (props: SpeciesInfoModalProps) => {
    const { commonName, scientificName, iucnStatus, onClose, initialTab, totalSiteCount } = props;

    const [siteLens, setSiteLens] = useState<SiteLens>(initialTab ?? 'all');
    const { currentCustomerId } = useCustomer();
    const [speciesCmsDetails, setSpeciesCmsDetails] = useState<CMSSpecies | null | undefined>(null);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({ fixedHeader: true, rowsPerPage: 10 });
    const [showBookCallModal, setShowBookCallModal] = useBoolean(false);
    const [isBioExpanded, setBioExpanded] = useBoolean(false);
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);

    // Source-aware: the grid-backed species page mounts the same modal inside a
    // provider, and the two responses are normalised to one shape.
    const { data: currentData, isFetching } = useSpeciesDetail({
        customerId: currentCustomerId,
        scientificName: scientificName,
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
    });

    const occursAt = currentData?.assessments?.length ?? 0;
    const invasiveAt = (currentData?.assessments ?? []).filter(asset => asset.isInvasive).length;
    // The grid can return a category the IUCN table has no entry for (UNKNOWN);
    // the chip would render nothing, and a label over an empty slot reads as broken.
    const hasIucnStatus = !!iucnStatus && !!iucnStatusData[iucnStatus];
    const onRedList = isRedListCategory(iucnStatus ?? '');
    // On the Red List at a site = CR/EN/VU/NT and not invasive there (DD-5480).
    const redListAt = onRedList ? occursAt - invasiveAt : 0;
    // Coverage as the list pages state it: floor(occupied / portfolio sites).
    const coveragePercent = totalSiteCount && totalSiteCount > 0 ? Math.floor((occursAt / totalSiteCount) * 100) : null;

    // A lens that applies to this species gets a tab; the others would be a
    // claim about the species the data does not make (a Red List tab on an LC
    // species, an invasive tab on a species invasive nowhere).
    const lensTabs = [
        { id: 'all', label: `All sites (${occursAt})` },
        ...(redListAt > 0 ? [{ id: 'redList', label: `Red List sites (${redListAt})` }] : []),
        ...(invasiveAt > 0 ? [{ id: 'invasive', label: `Invasive risk sites (${invasiveAt})` }] : []),
    ];
    // A requested lens that turned out not to apply falls back to all sites.
    const activeLens: SiteLens = lensTabs.some(tab => tab.id === siteLens) ? siteLens : 'all';

    const inLens = (asset: { isInvasive: boolean }) =>
        activeLens === 'invasive' ? asset.isInvasive : activeLens === 'redList' ? !asset.isInvasive : true;

    const onLensChange = (lens: string) => {
        setSiteLens(lens as SiteLens);
        // A stale page of a shorter list would show an empty table.
        setTableState(prev => ({ ...prev, currentPageNumber: 1 }));
    };

    useEffect(() => {
        if (!currentData) {
            return;
        }

        const tableData: DataTableState['data'] = [];

        // Only the sites the active lens selects, so the table always agrees
        // with the count on its tab.
        const speciesAssetsList = [...(currentData.assessments || [])].filter(inLens);

        speciesAssetsList
            .sort((a, b) => {
                const aConfidence = a.predictionLevel.toLowerCase();
                const bConfidence = b.predictionLevel.toLowerCase();
                return (confidenceOrder[aConfidence] ?? 99) - (confidenceOrder[bConfidence] ?? 99);
            })
            .forEach(asset => {
                const siteName = (
                    <div className='flex flex-col'>
                        <div className='text-sm'>{asset.projectName}</div>
                        <div className='text-xs text-grey-80'>{asset.country}</div>
                    </div>
                );

                const confidenceBadge = (
                    <Badge className={getConfidenceDetail(asset.predictionLevel).colorClass}>
                        {getConfidenceDetail(asset.predictionLevel).label}
                    </Badge>
                );

                // Display 'Detected/Undetected' badge depending on whether species found in Uploaded data
                // Display 'Upload data' badge if there is no uploaded data
                let uploadedDataBadgeType = DetectionBadgeType.UploadData;
                if (asset.hasUploadedData) {
                    uploadedDataBadgeType = asset.foundInUploadedData ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
                }

                // Display 'Detected/Undetected' badge depending on whether species found in NM Samples
                // Display 'Book a call' badge if there is no NM Samples data
                let nmSamplesBadgeType = DetectionBadgeType.BookCall;
                if (asset.hasEDNAData) {
                    nmSamplesBadgeType = asset.foundInEDNA ? DetectionBadgeType.Detected : DetectionBadgeType.Undetected;
                }

                tableData.push([siteName, confidenceBadge, nmSamplesBadgeType, uploadedDataBadgeType]);
            });

        setTableState(prev => ({
            ...prev,
            data: tableData,
        }));
    }, [currentData, activeLens]);

    useEffect(() => {
        if (!scientificName) {
            return;
        }

        const fetchSpeciesDetails = async () => {
            // Get species description from CMS
            const details = await cmsService.getSpeciesByScientificName(scientificName, 'fields[0]=description');

            // set undefined to indicate loading is complete
            setSpeciesCmsDetails(details ? details : undefined);
        };

        if (!speciesCmsDetails) {
            fetchSpeciesDetails();
        }
    }, [scientificName]);

    const speciesImage = (
        <SpeciesImage scientificName={scientificName} alt={scientificName} width='232px' height='130px' className='rounded-lg' />
    );

    // show loader until CMS details are fetched
    const isLoading = speciesCmsDetails === null || isFetching;

    const tableProps = {
        state: tableState,
        columns: tableColumns,
        onStateChange: setTableState,
        emptyStateMessage: `No Species Found`,
        className: 'overflow-visible',
        tableClassName: 'h-full',
    };

    // Check table rows for the current page for any site with no eDNA data
    // If found, display upsell for booking a call next to species description.
    const rowsThisPage = (tableState.data || []).slice(
        ((tableState.currentPageNumber || 1) - 1) * (tableState.rowsPerPage || 10),
        ((tableState.currentPageNumber || 1) - 1) * (tableState.rowsPerPage || 10) + (tableState.rowsPerPage || 10)
    );
    const hasSiteWithNoEdnaData = rowsThisPage.some(row => row[2] === DetectionBadgeType.BookCall);

    return (
        <Modal dismissOnClickOutside={true} classNames={{ body: 'w-[860px] max-h-4/5 p-6' }} onClose={onClose}>
            <Show when={!isLoading} fallback={<Loader />}>
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-row gap-4'>
                        {speciesImage}
                        <div className='flex flex-col justify-between'>
                            <div className='flex flex-col gap-1'>
                                {commonName && <div className='text-3xl text-primary'>{commonName}</div>}
                                {scientificName && <div className='text-base text-grey-80 italic'>({scientificName})</div>}
                            </div>
                            <div className='flex flex-row gap-8'>
                                {hasIucnStatus && (
                                    <div className='flex flex-col gap-1'>
                                        <div className='flex flex-col text-sm text-grey-80'>
                                            <div className='flex'>
                                                IUCN status{' '}
                                                <QuestionButtonHelp
                                                    icon={<CircleQuestionMark className='text-grey-80 h-4' />}
                                                    type='api'
                                                    placement='right'
                                                    slug='species-risk-site-status'
                                                    zIndex={1300}
                                                />
                                            </div>
                                        </div>
                                        <div className='flex flex-col justify-center'>
                                            {/* Not the shared IUCN chip: it renders off-Red-List categories as
                                                transparent grey text, which in this header reads as a missing
                                                value. Red for the Red List, a visible neutral chip otherwise. */}
                                            <StatusChip
                                                status={onRedList ? Status.ERROR : Status.DISABLED}
                                                label={iucnStatusData[iucnStatus as string].label}
                                                className={`text-xs max-w-fit ${onRedList ? '' : '!text-black'}`}
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* Absence means not invasive anywhere — no invented "None" state. */}
                                {invasiveAt > 0 && (
                                    <div className='flex flex-col gap-1'>
                                        <div className='text-sm text-grey-80'>Invasive risk</div>
                                        <div className='flex flex-col justify-center'>
                                            <StatusChip status={Status.INVASIVE} label='Invasive' className='text-xs' />
                                        </div>
                                    </div>
                                )}
                                <div className='flex flex-col gap-1'>
                                    <div className='flex flex-col justify-center text-sm text-grey-80'>
                                        <div className='flex'>
                                            Sites{' '}
                                            <QuestionButtonHelp
                                                icon={<CircleQuestionMark className='text-grey-80 h-4' />}
                                                type='api'
                                                placement='right'
                                                slug='species-risk-site-coverage'
                                                zIndex={1300}
                                            />
                                        </div>
                                    </div>
                                    <div className='text-base text-black'>
                                        {coveragePercent !== null ? (
                                            <>
                                                {coveragePercent}% ({occursAt} site{occursAt === 1 ? '' : 's'})
                                            </>
                                        ) : (
                                            <>
                                                Occurs at {occursAt} site{occursAt === 1 ? '' : 's'}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Identity context, so it reads with the name and image — clamped so
                        the site data stays in view, expanding in place on demand. */}
                    {speciesCmsDetails?.description && (
                        <div className='text-sm text-grey-80'>
                            <div
                                className={isBioExpanded ? '' : 'line-clamp-2'}
                                dangerouslySetInnerHTML={{ __html: speciesCmsDetails.description }}
                            ></div>
                            <button type='button' className='text-primary' onClick={setBioExpanded.toggle}>
                                {isBioExpanded ? 'Show less' : 'Read more'}
                            </button>
                        </div>
                    )}
                    <div className='border-t border-grey-20 h-[1px]'>&nbsp;</div>
                    <div className='flex flex-col gap-2'>
                        <div className='flex flex-col gap-1'>
                            <div className='text-xl text-black'>Site Presence Assessment</div>
                            <div className='border-b border-grey-20'>
                                <Tabs
                                    tabs={lensTabs}
                                    selectedTab={activeLens}
                                    onTabChange={onLensChange}
                                    classNames={{
                                        tabsContainer: 'flex items-center',
                                        default: 'px-3 py-2 text-sm text-black border-b-2 border-[transparent]',
                                        selected: 'text-primary border-b-2 !border-primary',
                                    }}
                                />
                            </div>
                            <div className='text-sm text-grey-80'>
                                {activeLens === 'invasive'
                                    ? "Species classified as invasive in at least one site's location"
                                    : activeLens === 'redList'
                                    ? 'Sites where Red Listed species is predicted and not invasive'
                                    : 'The likelihood of the species occurring at each site'}
                            </div>
                        </div>

                        <DataTable {...tableProps} />
                    </div>
                    {hasSiteWithNoEdnaData && (
                        <div className='flex flex-col gap-2 p-4 bg-grey-0 border border-grey-20 rounded-lg text-sm h-[fit-content]'>
                            Increase your data quality with ground-truthed eDNA & bioacoustics species insights.
                            <PrimaryButton className='w-[120px]' onClick={setShowBookCallModal.on}>
                                Book call <ArrowRight className='text-white' />
                            </PrimaryButton>
                            {showBookCallModal && <BookCallModal onClose={setShowBookCallModal.off} />}
                        </div>
                    )}
                </div>
            </Show>
        </Modal>
    );
};

export default SpeciesInfoModal;
